import type { ConnectorSourceJourneysObjectMapping, Source } from '../sources';
import type { StatusDetails } from '@/features/pipelines/utils';
import type { JsonObject } from '@/utils/json';

export const DestinationMode = {
  Cloud: 'cloud',
  Direct: 'device',
  Javascript: 'legacy',
  // Temporary while we build support for direct ETL destinations.
  StaticEtl: 'static-etl',
} as const;
export type DestinationMode = (typeof DestinationMode)[keyof typeof DestinationMode];

export interface Destination {
  id: number;
  enabled: boolean;
  mode: DestinationMode;
  name: string;
  option_id: string;
  created_by: number;
  updated_by: number;
  created_at: number;
  updated_at: number;
  source_ids: number[];
  settings: JsonObject;
  subscriptions_count: number;
  has_cio_destinations: boolean;
  oauth_connected?: boolean;
}

export interface AvailableDestination {
  option_id: string;
  icon: string;
  name: string;
  content: string;
  learn_more_url: string;
  category: string;
  mode: DestinationMode;
  experimental: boolean;
  premium?: boolean;
  beta: boolean;
  connection_type: string;
}

export interface DestinationMetrics {
  timeseries_first_try: [number, number][];
  timeseries_retry: [number, number][];
  timeseries_failed: [number, number][];
  timeseries_latency: [number, number][];
  delivered_first_try: number;
  delivered_retry: number;
  failed: number;
  latency: string;
}

export interface DestinationEvent {
  id: string;
  created_at: number;
  updated_at: number;
  workspace_id: number;
  destination_id: number;
  source_id: number;
  source_destination_id: number;
  destination_subscription_id: number;
  message_ids: string[];
  positions: string[];
  kmqServer_id: string;
  error?: DestinationEventProcessingError;
  execution_state?: DestinationEventExecutionState[];
  multi_status_results?: DestinationEventMultiStatusResult[];
  finished: boolean;
  store_server_id: string;
  store_key: string;
  url?: string;
}

export interface DestinationEventMultiStatusResult {
  status: number;
  error_type?: string;
  error_message?: string;
  error_reporter?: string;
  request_body?: string;
}

export interface DestinationEventExecutionState {
  request: DestinationEventRequest;
  response: DestinationEventResponse;
}

export interface DestinationEventRequest {
  url?: string;
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}

export interface DestinationEventResponse {
  status_code?: number;
  headers?: Record<string, string>;
  body?: string;
  error?: string;
  errorType?: DestinationErrorType;
  latency?: number;
}

export interface DestinationEventProcessingError {
  message: string;
  status_code: number;
}

export interface DestinationTestExchange {
  workspace_id: number;
  destination_id: number;
  destination_subscription_id: number;
  error?: DestinationEventProcessingError;
  execution_state: DestinationEventExecutionState[];
  finished: boolean;
}

export interface DestinationSubscription {
  id: number;
  action: string;
  destination_id: number;
  enabled: boolean;
  mapping: string;
  name: string;
  subscribe: string;
  created_at: number;
  updated_at: number;
  settings?: JsonObject;
}

export interface DestinationExport {
  id: number;
  subscription_id: number;
  type: string;
  filename?: string;
  url?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'discarded';
  total_records_processed: number;
  error?: DestinationSyncError;
  created_at: number;
  updated_at: number;
  started_at: number;
  ended_at: number;
  uploaded_at: number;
}

export interface DestinationSyncError {
  message: string;
}

export const DestinationErrorType = {
  ERRORS_RETRY_UNKNOWN: 'ERRORS_RETRY_UNKNOWN',
  ERRORS_DISCARDED_400: 'ERRORS_DISCARDED_400',
  ERRORS_DISCARDED_401: 'ERRORS_DISCARDED_401',
  ERRORS_DISCARDED_403: 'ERRORS_DISCARDED_403',
  ERRORS_DISCARDED_INVALID_SETTINGS: 'ERRORS_DISCARDED_INVALID_SETTINGS',
  ERRORS_DISCARDED_MESSAGE_REJECTED: 'ERRORS_DISCARDED_MESSAGE_REJECTED',
  ERROR_RETRY_429: 'ERROR_RETRY_429',
  ERROR_RETRY_5XX: 'ERROR_RETRY_5XX',
  ERROR_RETRY_NETWORK: 'ERROR_RETRY_NETWORK',
  ERROR_OTHER: 'ERROR_OTHER',
  ERROR_NO_MATCHING_DESTINATION: 'ERROR_NO_MATCHING_DESTINATION',
  NO_ERROR: 'NO_ERROR',
  ERROR_RETRY_OTHER_4XX: 'ERROR_RETRY_OTHER_4XX',
  ERROR_PARTIAL_SUCCESS_207: 'ERROR_PARTIAL_SUCCESS_207',
} as const;
export type DestinationErrorType = (typeof DestinationErrorType)[keyof typeof DestinationErrorType];

export interface DestinationError {
  error_type: DestinationErrorType;
  count: number;
  timestamp: number;
}

export interface DestinationErrorsSummary {
  errors: DestinationError[];
}

export interface DestinationErrorSample {
  destination_id: number;
  source_id: number;
  source_destination_id: number;
  server_id: string;
  id: string;
  error_type: DestinationErrorType;
  description?: string;
  timestamp: number;
}

export interface DestinationErrorSamples {
  errors: DestinationErrorSample[];
}

export interface ConnectableSource extends Source {
  status: StatusDetails | null;
  isCompatible: boolean;
  incompatibilityReason?: string;
}

export interface ConfigurableSourceConnection {
  source: Source;
  connection_settings?: ConnectorSourceJourneysObjectMapping;
}
